import React, { useCallback, useEffect, useState } from 'react';
import AloModal from '../YogaUI/alo-ui/AloModal';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import { usePromise } from '../../react-hooks';
import { isInGeotarget } from 'src/scripts/utils/location';
import { SignInModalData, SignInModalTab } from './types';
import { getPastEvent } from '../../../../utils/events';
import { OPEN_SIGN_IN_MODAL } from './utils';
import { getCookie, setCookie } from '../../../../utils/cookies';
import { InitialUrlSearchParams } from '../../../../utils/environment';
import { IS_NATIVE_APP_CLIENT } from '../../../../utils/helpers';
import AuthForm from '../AuthForm/AuthForm';
import { removeRsvpTokens } from 'src/scripts/utils/rsvp-events';

import './styles.scss';

const SignInModal = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<SignInModalData | null>(null);
  const signInModalSettings = window.getSignInModalSettings?.();
  const { data: isInUS, isLoading } = usePromise(() => isInGeotarget({ countries: ['US'] }));
  const _isInUS = ShippingCountryState.countryCode ? ShippingCountryState.countryCode === 'US' : isInUS;

  const [defaultTabOverride, setDefaultTabOverride] = useState<SignInModalTab | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<SignInModalTab | null>(null);

  const switchTab = (tab: SignInModalTab) => {
    setActiveTab(tab);
  };

  const openModal = useCallback((data: SignInModalData) => {
    setOpen(true);
    setData(data);
    data.tab && setDefaultTabOverride(data.tab);
  }, []);

  const closeModal = () => {
    setCookie('SignInModalDismissed', '1', undefined);
    setOpen(false);
    setData(null);
    setDefaultTabOverride(undefined);
    removeRsvpTokens();
  };

  useEffect(() => {
    document.addEventListener(OPEN_SIGN_IN_MODAL, (e) => {
      openModal((e as CustomEvent).detail);
    });
    let pastEvent = getPastEvent(OPEN_SIGN_IN_MODAL);
    if (pastEvent) openModal((pastEvent as CustomEvent).detail);

    return () => {
      document.removeEventListener(OPEN_SIGN_IN_MODAL, (e) => {
        openModal((e as CustomEvent).detail);
      });
    };
  }, [openModal]);

  // Handle AutoShow setting and open modal by URL params
  const { autoShow, closeButtonColor } = signInModalSettings || {};

  useEffect(() => {
    const aloSignInModal = document.documentElement;
    if (aloSignInModal) {
      aloSignInModal.style.setProperty('--sign-in-modal-close-icon-color', closeButtonColor ?? 'var(--white)');
    }
  }, [closeButtonColor]);

  useEffect(() => {
    const userDidDismissedModal = !!getCookie('SignInModalDismissed');

    if (!IS_NATIVE_APP_CLIENT && autoShow && !userDidDismissedModal) {
      setOpen(true);
      setData({ returnUrl: window.location.pathname });
    } else {
      if (InitialUrlSearchParams?.get('signIn')) {
        openModal({ returnUrl: window.location.pathname, tab: 'sign-in' });
      } else if (InitialUrlSearchParams?.get('startNow')) {
        openModal({ returnUrl: window.location.pathname, tab: 'create-account' });
      }
    }
  }, [autoShow, openModal]);

  if (isLoading) return null;
  return (
    <AloModal
      open={open}
      onClose={closeModal}
      MUIModalProps={{ disablePortal: true }}
      noPadding={true}
      wrapperClass={`sign-in-modal ${_isInUS ? 'domestic' : 'international'} ${activeTab}`}
    >
      <AuthForm
        returnUrl={data?.returnUrl}
        isSignInModal={true}
        defaultTabOverride={defaultTabOverride}
        onTabSwitchCallback={switchTab}
      />
    </AloModal>
  );
};

export default SignInModal;
